import { keepPreviousData, useQueries } from "@tanstack/react-query";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import { AuditRolesCollection, fetchAuditRoles } from "PFCore/services/audit_planner";
import { Id, OrderOption, OrderOptionOrEmpty, QueryOptions, QueryParams } from "PFCore/types";
import { useMemo } from "react";

import { auditRolesKeys } from "./query_keys";

type UseEngagementsQuery = {
  options?: QueryOptions<AuditRolesCollection>;
  auditEngagementData: {
    auditEngagementId: Id;
    enabled: boolean;
    page: number;
  }[];
  params?: {
    rolesOrder?: Record<string, OrderOptionOrEmpty>;
  } & Omit<QueryParams, "page" | "order">;
};

export const useAuditRolesByEngagementsQuery = ({
  auditEngagementData: auditEngagementIds,
  options,
  params
}: UseEngagementsQuery) => {
  const { filters, perPage, rolesOrder } = params || {};
  const queriesConfig = useMemo(
    () =>
      auditEngagementIds.map(({ auditEngagementId, enabled, page }) => {
        const order = isEmpty(rolesOrder?.[auditEngagementId])
          ? undefined
          : (rolesOrder?.[auditEngagementId] as OrderOption);
        return {
          queryKey: auditRolesKeys.withParameters(filters, order, auditEngagementId, page),
          queryFn: async () => {
            const finalFilters = merge(cloneDeep(filters), {
              fields: {
                parent_activity_id: auditEngagementId
              }
            });

            return fetchAuditRoles({ filters: finalFilters, page, perPage, order });
          },
          placeholderData: keepPreviousData,
          ...options,
          enabled: enabled && options?.enabled
        };
      }),
    [auditEngagementIds, rolesOrder, filters, options, perPage]
  );

  const queries = useQueries({
    queries: queriesConfig
  });

  const isLoadingByEngagement = useMemo(
    () =>
      Object.fromEntries(
        queries.map(({ isLoading, isFetching }, index) => [
          auditEngagementIds[index].auditEngagementId,
          isLoading || isFetching
        ])
      ),
    [queries, auditEngagementIds]
  );

  const rolesByEngagement = useMemo(
    () =>
      Object.fromEntries(
        queries.map(({ data }, index) => [auditEngagementIds[index].auditEngagementId, data])
      ),
    [queries, auditEngagementIds]
  );

  return {
    rolesByEngagement,
    isLoadingByEngagement
  };
};
